if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // DVK: the form is novalidate, so a typed 0, negative or empty quantity would reach
        // /cart/add (Shopify adds nothing and still answers 200). Clamp to the minimum first.
        const qtyInput = this.form.elements.quantity;
        if (qtyInput && qtyInput.value !== undefined) {
          const minQty = Math.max(parseInt(qtyInput.dataset.min || qtyInput.min) || 1, 1);
          const typedQty = parseInt(qtyInput.value);
          if (isNaN(typedQty) || typedQty < minQty) {
            qtyInput.value = minQty;
            qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              // DVK: Shopify can add part of the quantity (e.g. "Only 1 item was added to your cart
              // due to availability.") and still answer 422. Say so in Dutch and refresh the cart UI.
              // The store language is Dutch, so Shopify answers "Er zijn slechts 7 artikelen aan je
              // winkelwagen toegevoegd ..."; keep the English pattern for other locales.
              const partialText = `${response.description || ''} ${response.message || ''}`;
              const partialMatch =
                partialText.match(/only (\d+) items? (?:was|were) added/i) ||
                partialText.match(/slechts (\d+) artikel(?:en)? .*toegevoegd/i);
              const partialAdd = partialMatch && [partialMatch[0], partialMatch[1]];
              let errorText = response.description;
              if (partialAdd) {
                const wrapper = this.querySelector('.product-form__error-message-wrapper');
                const target = wrapper?.querySelector('.product-form__error-message');
                const dutch = wrapper?.dataset.dvkMsgPartial?.replace('__N__', partialAdd[1]);
                if (dutch && target) {
                  // Mark as already translated so the cart-error translators leave it alone.
                  target.dataset.dvkTranslated = dutch;
                  errorText = dutch;
                }
                this.refreshCartAfterPartialAdd();
              }
              this.handleErrorMessage(errorText);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              // Settle with the real cart state (a partial add did change it). Rejecting here surfaces
              // as an uncaught English error through third-party listeners that chain on the promise;
              // the failure itself is reported through CartErrorEvent above.
              this.resolveCartLinesUpdate(linesUpdateDeferred);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      refreshCartAfterPartialAdd() {
        if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form-partial' });
          return;
        }
        const sections = this.cart.getSectionsToRender();
        const ids = [...new Set(sections.map((section) => section.section || section.id))];
        fetch(`${routes.cart_url}?sections=${ids.join(',')}`)
          .then((response) => response.json())
          .then((parsed) => {
            sections.forEach((section) => {
              const html = parsed[section.section || section.id];
              if (!html) return;
              const target = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              const source = new DOMParser()
                .parseFromString(html, 'text/html')
                .querySelector(section.selector || '.shopify-section');
              if (target && source) target.innerHTML = source.innerHTML;
            });
            this.cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
            this.cart.classList.remove('is-empty');
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form-partial' });
          })
          .catch((e) => console.error(e));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        // A rejected add (422 stock limit) is already shown to the shopper in Dutch; mark the
        // promise as handled so it does not surface as an uncaught (English) error.
        deferred.promise?.catch?.(() => {});
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
